import {
  AlertTriangle,
  CircleDashed,
  Clock,
  Eye,
  GitBranch,
  Grid3x3,
  Layers,
  LayoutList,
  Plus,
  Scissors,
  Search,
  Star,
  TrendingUp,
  User,
} from "lucide-react";
import React from "react";
import { toast } from "sonner";
import { useMyScenarios } from "../../hooks/useMyScenarios";
import { usePermissions } from "../../hooks/usePermissions";
import { useScenarios } from "../../hooks/useScenarios";
import {
  enrichScenarioViews,
  useScenarioViews,
} from "../../hooks/useScenarioViews";
import { useTerminology } from "../../hooks/useTerminology";
import { useVisibleScenarios } from "../../hooks/useVisibleScenarios";
import { formatDate } from "../../lib/utils";
import { ComprehensiveScenario } from "../../types/scenarios";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { BaseForecastCard } from "./BaseForecastCard";
import { ScenarioActionsMenu } from "./ScenarioActionsMenu";
import { ScenarioCard } from "./ScenarioCard";
import { ScenarioFreshnessBadge } from "./ScenarioFreshnessBadge";
import { ScenarioCardGridSkeleton } from "./ScenarioSkeletons";
import { categoryLabels, resolveCategory } from "./scenarioCategory";

interface ScenarioListGalleryProps {
  onSelectScenario: (id: string) => void;
  onEditScenario?: (id: string) => void;
  onCompareScenario?: (id: string) => void;
  onCreateScenario?: (template?: "growth" | "cost-cutting" | "crisis") => void;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  categoryFilter: string;
  setCategoryFilter: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  sortBy: "recent" | "name" | "impact";
  setSortBy: (v: "recent" | "name" | "impact") => void;
  viewMode: "grid" | "list";
  setViewMode: (v: "grid" | "list") => void;
}

export const ScenarioListGallery: React.FC<ScenarioListGalleryProps> = ({
  onSelectScenario,
  onCompareScenario,
  onCreateScenario,
  searchTerm,
  setSearchTerm,
  categoryFilter,
  setCategoryFilter,
  statusFilter,
  setStatusFilter,
  sortBy,
  setSortBy,
  viewMode,
  setViewMode,
}) => {
  const { createScenario, cloneScenario } = useScenarios();
  const { getScenarioTerm } = useTerminology();
  const { hasPermission } = usePermissions();
  // Per engine spec §9.2, archived scenarios are only visible in the list to
  // the manager view — gated here on the same permission as the Parked tab.
  const canViewArchived = hasPermission("view_parked_scenarios");
  const contextScenarios = useVisibleScenarios();
  // "Your Scenarios" is scoped to owned + explicitly-shared scenarios only —
  // narrower than the broad visibility rules other views rely on.
  const { isMyScenario } = useMyScenarios();

  // Live mode: source membership, ordering and persisted metrics from GraphQL
  // (vw_Scenarios) with combined server-side filter + sort. Demo mode keeps the
  // existing client-side filtering path below.
  const liveView = useScenarioViews({
    search: searchTerm,
    category: categoryFilter,
    status: statusFilter,
    sortBy,
  });
  // Unfiltered live set — used to compute the category/status pill counts so the
  // badges reflect the full library, not the currently applied filter.
  const liveAll = useScenarioViews({ enabled: liveView.isLive });

  // Scope to owned + explicitly-shared. Applied AFTER enrichScenarioViews so
  // context scenarios appended by the enrich step are filtered too. This is the
  // "all" set that drives the pill counts and the empty state.
  const scenarios = (
    liveView.isLive
      ? enrichScenarioViews(liveAll.scenarios, contextScenarios)
      : contextScenarios
  ).filter(isMyScenario);

  // Handle Clone
  const handleClone = async (id: string) => {
    const scenario = scenarios.find((s) => s.id === id);
    if (!scenario) return;

    await cloneScenario(id);
    toast.success(`"${scenario.name}" has been cloned`);
  };

  // Handle Clone Base — creates a new Draft scenario seeded from base data
  const handleCloneBase = async () => {
    const baseClone = {
      name: "Base Forecast (Clone)",
      description:
        "Cloned from the Base Forecast. Adjust this scenario to model a what-if view.",
      status: "draft" as const,
      category: "custom" as const,
      priority: "medium" as const,
      isFavorite: false,
      commentCount: 0,
      comments: [],
      shares: [],
      collectionIds: [],
      tags: ["base-clone"],
      changes: [],
      metrics: {
        netImpact: 0,
        minCash: 0,
        runway: 0,
        probabilityOfSuccess: 100,
      },
      approvals: [],
      versions: [],
    };
    await createScenario(
      baseClone as unknown as Parameters<typeof createScenario>[0],
    );
  };

  // Filter + sort scenarios.
  // Live mode: the server already applied the combined filter + sort, so use the
  // enriched view results directly. Demo mode: filter + sort client-side.
  // When any server-side filter is active, the GraphQL view is authoritative for
  // membership — don't append context-only scenarios (they'd bypass the filter).
  const hasActiveServerFilter =
    searchTerm.trim() !== "" ||
    categoryFilter !== "all" ||
    statusFilter !== "all";
  let filteredScenarios: ComprehensiveScenario[];
  if (liveView.isLive) {
    filteredScenarios = enrichScenarioViews(
      liveView.scenarios,
      contextScenarios,
      {
        appendContextOnly: !hasActiveServerFilter,
      },
    ).filter(isMyScenario);
  } else {
    filteredScenarios = scenarios.filter((scenario) => {
      // Search filter
      if (
        searchTerm &&
        !scenario.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !scenario.description.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }

      // Category filter — an unset category is filtered as 'custom' ("Not Set")
      if (
        categoryFilter !== "all" &&
        resolveCategory(scenario) !== categoryFilter
      ) {
        return false;
      }

      // Status filter — all 8 statuses
      if (statusFilter !== "all") {
        return scenario.status === statusFilter;
      }

      return true;
    });

    // Sort scenarios — favourites always float to the top, then apply chosen sort within each group
    filteredScenarios = [...filteredScenarios].sort((a, b) => {
      // Primary: favourites first
      if (a.isFavorite !== b.isFavorite) {
        return a.isFavorite ? -1 : 1;
      }
      // Secondary: chosen sort order within group
      if (sortBy === "name") {
        return a.name.localeCompare(b.name);
      } else if (sortBy === "impact") {
        return Math.abs(b.metrics.netImpact) - Math.abs(a.metrics.netImpact);
      } else {
        return b.modifiedAt.getTime() - a.modifiedAt.getTime();
      }
    });
  }

  // Count by category. An unset category counts as 'custom' ("Not Set").
  const categoryOf = (s: ComprehensiveScenario) => resolveCategory(s);
  const categoryCounts = {
    all: scenarios.length,
    growth: scenarios.filter((s) => categoryOf(s) === "growth").length,
    "cost-cutting": scenarios.filter((s) => categoryOf(s) === "cost-cutting")
      .length,
    crisis: scenarios.filter((s) => categoryOf(s) === "crisis").length,
    custom: scenarios.filter((s) => categoryOf(s) === "custom").length,
  };

  // Count by status
  const statusCounts = {
    all: scenarios.length,
    draft: scenarios.filter((s) => s.status === "draft").length,
    "under-review": scenarios.filter((s) => s.status === "under-review").length,
    "pending-approval": scenarios.filter((s) => s.status === "pending-approval")
      .length,

    active: scenarios.filter((s) => s.status === "active").length,
    committed: scenarios.filter((s) => s.status === "committed").length,
    parked: scenarios.filter((s) => s.status === "parked").length,
    rejected: scenarios.filter((s) => s.status === "rejected").length,
    archived: scenarios.filter((s) => s.status === "archived").length,
  };

  // Live mode only: the gallery is fed by GraphQL, so hold the cards, counts and
  // empty state back until both queries settle rather than rendering zeroes.
  const isGalleryLoading =
    liveView.isLive && (liveView.isLoading || liveAll.isLoading);

  // Empty state — only once loading has finished, so it never flashes.
  if (!isGalleryLoading && scenarios.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-5">
          <GitBranch className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          No {getScenarioTerm(false)} yet
        </h3>
        <p className="text-gray-500 text-center max-w-sm mb-6">
          Create {getScenarioTerm(false)} to model different business outcomes
          and explore their impact on your cash flow.
        </p>
        <Button
          size="lg"
          className="gap-2"
          onClick={() => onCreateScenario?.()}
        >
          <Plus className="h-5 w-5" />
          New {getScenarioTerm(true, false)}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Base Forecast — always pinned at top */}
      <div>
        <BaseForecastCard
          onView={() => {
            /* Base view — TODO wire to base detail view */
          }}
          onCompare={() => {
            if (onCompareScenario) onCompareScenario("base");
          }}
          onClone={handleCloneBase}
        />
      </div>

      {/* Your Scenarios — divider + filters inline */}
      <div className="flex items-center gap-4">
        <h2 className="text-lg font-semibold text-neutral-900 whitespace-nowrap">
          Your {getScenarioTerm(true)}
        </h2>
        <div className="flex-1 border-t border-gray-200" />
      </div>

      {/* Filters Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={`Search ${getScenarioTerm(false)}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Category Pills */}
            <div className="flex items-center gap-2">
              {[
                {
                  value: "all",
                  label: "All",
                  icon: <Layers className="h-3.5 w-3.5" />,
                  count: categoryCounts.all,
                },
                {
                  value: "growth",
                  label: "Growth",
                  icon: <TrendingUp className="h-3.5 w-3.5" />,
                  count: categoryCounts.growth,
                },
                {
                  value: "cost-cutting",
                  label: "Cost-Cutting",
                  icon: <Scissors className="h-3.5 w-3.5" />,
                  count: categoryCounts["cost-cutting"],
                },
                {
                  value: "crisis",
                  label: "Crisis",
                  icon: <AlertTriangle className="h-3.5 w-3.5" />,
                  count: categoryCounts.crisis,
                },
                {
                  value: "custom",
                  label: categoryLabels.custom,
                  icon: <CircleDashed className="h-3.5 w-3.5" />,
                  count: categoryCounts.custom,
                },
              ].map(({ value, label, icon, count }) => (
                <Button
                  key={value}
                  variant={categoryFilter === value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCategoryFilter(value)}
                  className="gap-1.5"
                >
                  {icon}
                  {label}
                  {isGalleryLoading ? (
                    <Skeleton className="ml-0.5 h-4 w-5 rounded-full" />
                  ) : (
                    <Badge variant="secondary" className="ml-0.5">
                      {count}
                    </Badge>
                  )}
                </Button>
              ))}
            </div>

            {/* Status Filter — all 8 statuses */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All Status{isGalleryLoading ? "" : ` (${statusCounts.all})`}
                </SelectItem>
                <SelectItem value="draft">
                  Draft{" "}
                  {statusCounts.draft > 0 ? `(${statusCounts.draft})` : ""}
                </SelectItem>
                <SelectItem value="under-review">
                  Under Review{" "}
                  {statusCounts["under-review"] > 0
                    ? `(${statusCounts["under-review"]})`
                    : ""}
                </SelectItem>
                <SelectItem value="pending-approval">
                  Pending Approval{" "}
                  {statusCounts["pending-approval"] > 0
                    ? `(${statusCounts["pending-approval"]})`
                    : ""}
                </SelectItem>
                <SelectItem value="active">
                  Active{" "}
                  {statusCounts.active > 0 ? `(${statusCounts.active})` : ""}
                </SelectItem>
                <SelectItem value="committed">
                  Committed{" "}
                  {statusCounts.committed > 0
                    ? `(${statusCounts.committed})`
                    : ""}
                </SelectItem>
                <SelectItem value="parked">
                  Parked{" "}
                  {statusCounts.parked > 0 ? `(${statusCounts.parked})` : ""}
                </SelectItem>
                <SelectItem value="rejected">
                  Rejected{" "}
                  {statusCounts.rejected > 0
                    ? `(${statusCounts.rejected})`
                    : ""}
                </SelectItem>
                {canViewArchived && (
                  <SelectItem value="archived">
                    Archived{" "}
                    {statusCounts.archived > 0
                      ? `(${statusCounts.archived})`
                      : ""}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Recently Updated</SelectItem>
                <SelectItem value="name">
                  {getScenarioTerm(true)} Name
                </SelectItem>
                <SelectItem value="impact">Net Impact</SelectItem>
              </SelectContent>
            </Select>

            {/* View Toggle */}
            <div className="flex gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className="px-3"
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className="px-3"
              >
                <LayoutList className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        {isGalleryLoading ? (
          <Skeleton className="h-4 w-48" />
        ) : (
          <p className="text-sm text-gray-600">
            Showing{" "}
            <span className="font-semibold">{filteredScenarios.length}</span> of{" "}
            <span className="font-semibold">{scenarios.length}</span>{" "}
            {getScenarioTerm(false)}
          </p>
        )}
        {(searchTerm || categoryFilter !== "all" || statusFilter !== "all") && (
          <button
            onClick={() => {
              setSearchTerm("");
              setCategoryFilter("all");
              setStatusFilter("all");
            }}
            className="text-sm text-primary hover:underline"
          >
            Clear all filters
          </button>
        )}
      </div>

      {/* Scenario Cards */}
      {isGalleryLoading ? (
        <ScenarioCardGridSkeleton />
      ) : filteredScenarios.length > 0 ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredScenarios.map((scenario) => (
              <ScenarioCard
                key={scenario.id}
                scenario={scenario}
                onSelect={onSelectScenario}
                onClone={handleClone}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-border rounded-lg border bg-white overflow-hidden">
            {filteredScenarios.map((scenario) => (
              <div
                key={scenario.id}
                onClick={() => onSelectScenario(scenario.id)}
                className="flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer"
                style={{
                  borderLeft: `3px solid var(--mc-${scenario.category === "growth" ? "success" : scenario.category === "crisis" ? "error" : scenario.category === "cost-cutting" ? "info" : "warning"}-border)`,
                }}
              >
                {/* Name + description */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 truncate">
                    {scenario.name}
                  </p>
                  <p className="text-xs text-neutral-500 truncate">
                    {scenario.description}
                  </p>
                  {/* Metadata */}
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-neutral-400">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {scenario.createdByName}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Created {formatDate(scenario.createdAt)}
                    </span>
                    <span>Modified {formatDate(scenario.modifiedAt)}</span>
                    <span>by {scenario.modifiedBy}</span>
                    <span>
                      {scenario.adjustmentCount ?? scenario.changes.length}{" "}
                      adjustments
                    </span>
                  </div>
                </div>

                {/* Category */}
                <Badge
                  variant="outline"
                  className="text-xs shrink-0 hidden md:flex"
                >
                  {categoryLabels[resolveCategory(scenario)]}
                </Badge>

                {/* Status */}
                <Badge
                  variant={
                    scenario.status === "active" ||
                    scenario.status === "committed"
                      ? "success"
                      : scenario.status === "pending-approval"
                        ? "warning"
                        : scenario.status === "rejected"
                          ? "outline"
                          : "secondary"
                  }
                  className="text-xs shrink-0 capitalize hidden sm:flex"
                >
                  {scenario.status.replace(/-/g, " ")}
                </Badge>

                {/* Freshness */}
                <ScenarioFreshnessBadge
                  scenario={scenario}
                  className="shrink-0 hidden sm:flex"
                />

                {/* Net impact */}
                <span
                  className={`text-xs font-semibold shrink-0 hidden lg:block ${scenario.metrics.netImpact >= 0 ? "text-green-600" : "text-neutral-700"}`}
                >
                  {scenario.metrics.netImpact >= 0 ? "+" : ""}
                  {(scenario.metrics.netImpact / 1000).toFixed(0)}K
                </span>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-xs gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectScenario(scenario.id);
                    }}
                  >
                    <Eye className="h-3.5 w-3.5" /> View
                  </Button>
                  <ScenarioActionsMenu
                    scenario={scenario}
                    onClone={handleClone}
                  />
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No {getScenarioTerm(false)} found
            </h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your filters or search terms
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm("");
                setCategoryFilter("all");
                setStatusFilter("all");
              }}
            >
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
